import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import {
  tasksApi, patientsApi, appointmentsApi, modulesApi,
  dashboardApi, notificationsApi, usersApi, authApi,
  anchorTypesApi, practicesApi,
} from '../lib/api';
import { pausePollingOnError } from '../lib/queryHelpers';
import { useAuthStore, useNotificationStore } from '../store';
import type { Task, Patient, DashboardMetrics, User, AnchorTypeConfig, PracticeStatus } from '../types';

// ─── AUTH HOOK ────────────────────────────────────────────────────────────────

export function useCurrentUser() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { setUser, setToken, setCurrentPractice, setAssignedPractices, setDeadlineZoneBasis } = useAuthStore();

  return useQuery({
    queryKey: ['current-user'],
    enabled: isAuthenticated,
    queryFn: async () => {
      // Get a Bearer from Auth0. If this throws (third-party cookies blocked,
      // silent-auth iframe failed, session expired server-side), surface the
      // error to the query state rather than silently continuing with an
      // empty token — otherwise every downstream query fires without an
      // Authorization header and we get a 401 flood.
      let token: string;
      try {
        token = await getAccessTokenSilently();
      } catch (e) {
        console.error('[auth] getAccessTokenSilently failed:', e);
        useAuthStore.getState().logout();
        throw e;
      }
      if (!token) {
        console.error('[auth] getAccessTokenSilently returned empty token');
        useAuthStore.getState().logout();
        throw new Error('Auth0 returned empty access token');
      }
      setToken(token);

      const res = await authApi.getMe();
      const userData = res.data.data as User & {
        practice_name?: string;
        assigned_practices?: any[];
        // The DEADLINE DAY BASIS pair (see store/index.ts resolveDeadlineTimeZone):
        // this user's own practice zone, and the default the SERVER resolved.
        practice_timezone?: string | null;
        deadline_timezone_default?: string;
        practice_status?: string | null;
      };

      // Record the day basis BEFORE the practice object is built: a DATE deadline is read on
      // the practice's calendar, else on the server's own default — never on the viewer's
      // zone, so an offshore (IST) reviewer and a US supervisor read the same case the same way.
      setDeadlineZoneBasis(userData.practice_timezone ?? null, userData.deadline_timezone_default);

      setUser({
        user_id: userData.user_id,
        practice_id: userData.practice_id,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        role: userData.role,
        // roles[]-retirement Wave 3c — carry the 5 access axes (identity union, rank, grants,
        // scope, license/org) so the store's union/rank/grants helpers read authoritative data
        // instead of always falling back to [role]. Byte-equivalent in single-role prod.
        roles: userData.roles,
        rank: userData.rank,
        responsibilities: userData.responsibilities,
        module_admin_for: userData.module_admin_for,
        // W4a/W3 — generic capability grants (e.g. records_release.approve); the UI gates the MRR
        // clinical approve/deny/review affordances on these instead of role-name strings.
        capabilities: userData.capabilities,
        scope_location_ids: userData.scope_location_ids,
        scope_module_keys: userData.scope_module_keys,
        license: userData.license,
        scope_organization_id: userData.scope_organization_id,
        is_org_level: userData.is_org_level,
        // Wave 1 — the DISTINCT cross-tenant PHI god signal (users.is_platform_admin).
        // Harmlessly undefined until the BE column ships; capabilities.isPlatformAdmin falls back to roles[] meanwhile.
        is_platform_admin: userData.is_platform_admin,
        user_type: userData.user_type,
        status: userData.status,
        login_method: userData.login_method,
        mfa_enabled: userData.mfa_enabled,
        last_login: userData.last_login,
        created_at: userData.created_at,
      });

      if (userData.assigned_practices?.length) {
        // Platform user WITH practice assignments. Keep the previously-selected
        // practice if it's still one they're assigned to — read it from the
        // persisted Zustand store (key 'previsit-auth'), NOT the legacy orphan
        // 'previsit_current_practice' key. Otherwise fall back to the user's HOME
        // practice if it is in the list (an org-level practice user; ai.346 S2 A),
        // else the first assigned practice.
        setAssignedPractices(userData.assigned_practices);
        const persisted = useAuthStore.getState().currentPractice;
        const match = persisted
          ? userData.assigned_practices.find(p => p.practice_id === persisted.practice_id)
          : undefined;
        const home = userData.practice_id
          ? userData.assigned_practices.find(p => p.practice_id === userData.practice_id)
          : undefined;
        setCurrentPractice(match || home || userData.assigned_practices[0]);
      } else if (userData.practice_id
        && (userData.practice_status === 'suspended' || userData.practice_status === 'terminated')) {
        // ai.359 F3 belt (defence in depth; A1's GET /me refusal is the real gate): never build the current practice from
        // an EXITED home when the list is empty - the server would refuse every request at it.
        setCurrentPractice(null);
      } else if (userData.practice_id) {
        setCurrentPractice({
          practice_id: userData.practice_id,
          name: userData.practice_name || 'My Practice',
          // The practice's own calendar (mig 206). The platform branch above gets it free
          // (assigned_practices rows are SELECT p.*); this branch built the practice object
          // WITHOUT the field, so a practice user's deadlines could not be judged on their
          // own calendar even when one was configured.
          timezone: userData.practice_timezone ?? null,
          specialty: '',
          status: (userData.practice_status as PracticeStatus | null | undefined) ?? 'active',
          npi_verified: false,
          email_verified: false,
          mobile_verified: false,
          subscription_status: 'active',
          config: { duplicate_detection: { fields: [] } },
          security_policy: { inactivity_lock_days: 30, session_timeout_mins: 15, max_failed_attempts: 3, password_expiry_days: 90, mfa_required: false },
          data_ownership_mode: (userData as any).data_ownership_mode ?? 'import_only',
          created_at: userData.created_at,
        });
      } else {
        // Platform user with NO practice assignments (pending-assignment operator)
        // and no own practice_id. Clear any practice carried over from a previous
        // session — otherwise the stale X-Practice-ID header drives a permanent
        // 403 PRACTICE_SWITCH_DENIED and the pollers storm the rate limiter (429).
        // This is the missing `else` that caused the live-prod request storm.
        setAssignedPractices([]);
        setCurrentPractice(null);
      }

      return userData;
    },
    // Cache the signed-in identity for the session: a re-mount or tab-focus must
    // NOT re-hit /auth/me, which floods the endpoint and trips the rate limiter
    // (the original staleTime:0 + refetchOnWindowFocus:true did exactly that).
    // A full reload / practice-switch / explicit invalidation refetches when
    // genuinely needed.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // Never retry the auth probe: on a rejected/expired token this would multiply
    // /auth/me calls and trip the auth rate limiter. One failed probe is enough —
    // the interceptor + LoginPage loop-breaker handle the rest.
    retry: false,
  });
}

// ─── TASK HOOKS ───────────────────────────────────────────────────────────────

export function useTasks(filters: Record<string, unknown> = {}) {
  const { isAuthenticated } = useAuth0();
  return useQuery({
    queryKey: ['tasks', filters],
    enabled: isAuthenticated,
    queryFn: async () => {
      const res = await tasksApi.getAll(filters);
      return res.data.data as Task[];
    },
    refetchInterval: 60000,
    // staleTime: inherited from global default (0)
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useTask(taskId: string | null) {
  const { isAuthenticated } = useAuth0();
  return useQuery({
    queryKey: ['task', taskId],
    enabled: isAuthenticated && !!taskId,
    queryFn: async () => {
      const res = await tasksApi.getById(taskId!);
      return res.data.data as Task;
    },
    // staleTime: inherited from global default (0)
    refetchOnWindowFocus: false,
  });
}

export function useTaskChildren(taskId: string | null, totalChildren: number) {
  const { isAuthenticated } = useAuth0();
  return useQuery({
    queryKey: ['task-children', taskId],
    enabled: isAuthenticated && !!taskId && totalChildren > 0,
    queryFn: async () => {
      const res = await tasksApi.getChildren(taskId!);
      return res.data.data as Task[];
    },
    // staleTime: inherited from global default (0)
    refetchOnWindowFocus: false,
  });
}

export function useUpdateTaskStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: string }) =>
      tasksApi.updateStatus(taskId, status),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useUpdateTaskOutcome() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: unknown }) =>
      tasksApi.updateOutcome(taskId, data),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

// ─── DASHBOARD HOOK ───────────────────────────────────────────────────────────

export function useDashboard(teamView = false) {
  const { user } = useAuthStore();   // only fire once token is in the store
  return useQuery({
    queryKey: ['dashboard', teamView],
    enabled: !!user?.user_id,
    queryFn: async () => {
      const res = await dashboardApi.getMetrics({ team_view: teamView });
      return res.data.data as DashboardMetrics;
    },
    // Pause polling while the query is failing so a permanent error can't hammer
    // the API (see pausePollingOnError). Was a bare `refetchInterval: 120000`.
    refetchInterval: pausePollingOnError(120000),
    // staleTime: inherited from global default (0)
    refetchOnWindowFocus: false,
    retry: false,
  });
}

// ─── PATIENTS HOOK ────────────────────────────────────────────────────────────

export function usePatients(search = '') {
  const { isAuthenticated } = useAuth0();
  return useQuery({
    queryKey: ['patients', search],
    enabled: isAuthenticated,
    queryFn: async () => {
      const res = await patientsApi.getAll({ search, limit: 100 });
      return res.data.data as Patient[];
    },
    // staleTime: inherited from global default (0)
    refetchOnWindowFocus: false,
  });
}

// ─── MODULES HOOK ─────────────────────────────────────────────────────────────

export function usePracticeModules() {
  const { isAuthenticated } = useAuth0();
  return useQuery({
    queryKey: ['practice-modules'],
    enabled: isAuthenticated,
    queryFn: async () => {
      const res = await modulesApi.getPracticeModules();
      return res.data.data as any[];
    },
    // staleTime: inherited from global default (0)
    refetchOnWindowFocus: false,
  });
}

// ─── USERS HOOK ───────────────────────────────────────────────────────────────

export function usePracticeUsers() {
  const { isAuthenticated } = useAuth0();
  return useQuery({
    queryKey: ['practice-users'],
    enabled: isAuthenticated,
    queryFn: async () => {
      const res = await usersApi.getAll();
      return res.data.data as User[];
    },
    // staleTime: inherited from global default (0)
    refetchOnWindowFocus: false,
  });
}

// ─── NOTIFICATIONS HOOK ───────────────────────────────────────────────────────

export function useNotifications() {
  const { user } = useAuthStore();   // only fire once token is in the store
  const { setNotifications } = useNotificationStore();
  return useQuery({
    queryKey: ['notifications'],
    enabled: !!user?.user_id,
    queryFn: async () => {
      const res = await notificationsApi.getAll();
      const data = res.data.data as any[];
      setNotifications(data);
      return data;
    },
    // Pause polling while the query is failing (see pausePollingOnError). NOTE:
    // NotificationBell.tsx holds a second observer on this same ['notifications']
    // key with a 30s interval — it MUST use the same guard, or the shared query
    // keeps polling at 30s and this guard is moot.
    refetchInterval: pausePollingOnError(60000),
    // staleTime: inherited from global default (0)
    refetchOnWindowFocus: false,
    retry: false,
  });
}

// ─── PRACTICE MODE HOOK ───────────────────────────────────────────────────────
// Returns whether this practice is in import_only or full_control mode.
// Use this anywhere in the UI to conditionally show/hide edit/add controls.
//
// Audit hardening (security pass): previously this read `data_ownership_mode`
// directly from the Zustand auth store, which is populated once at login from
// /auth/me and never refreshed. When a platform admin flipped the mode, the
// UI lock state stayed stale until the user logged out and back in — a real
// bug for "Add Patient" and the EMR-readonly banner. We now back the read
// with a React Query under ['practice-detail', practiceId] so the Admin page's
// invalidation on save propagates here automatically. Zustand is kept as a
// pre-resolution fallback so the first render isn't empty.
export function usePracticeMode() {
  const { currentPractice } = useAuthStore();
  const { data: practiceData } = useQuery({
    queryKey: ['practice-detail', currentPractice?.practice_id],
    queryFn: async () => {
      const r = await practicesApi.getById(currentPractice!.practice_id);
      return r.data.data;
    },
    enabled: !!currentPractice?.practice_id,
  });
  const mode = ((practiceData as any)?.data_ownership_mode
              ?? currentPractice?.data_ownership_mode
              ?? 'import_only') as 'import_only' | 'full_control';
  return {
    mode,
    isImportOnly: mode === 'import_only',
    isFullControl: mode === 'full_control',
  };
}

// ─── PER-PRACTICE ACCESS (access core, DESIGN 3.7) ─────────────────────────────
// useMyPracticeAccess (the caller's own answers at the SELECTED practice, GET /api/access/me), the
// /api/access query keys and the one after-a-change-set invalidation. They live in lib/capabilities
// beside canUsePracticeKey, so a door can read them without importing this module (which pulls in
// the Auth0 SDK); they are re-exported here so every hook is reachable from one place.
export { useMyPracticeAccess, accessQueryKeys, invalidateAfterAccessChange } from '../lib/capabilities';

// ─── ANCHOR TYPES ─────────────────────────────────────────────────────────────
// Global display config for the 5 task anchor types — loaded once per session.
// Every role can read this (needed for task-list badge rendering). Platform
// Admin edits flow through anchorTypesApi.update and invalidate this cache.
//
// staleTime is 5 minutes: these edits are rare (platform-level config) so we
// can avoid refetching on every navigation. The data is also embedded per-row
// in the task list response, so even if this hook hasn't yet fetched, badge
// rendering still works from row-level fields.

export function useAnchorTypes() {
  const token = useAuthStore(s => s.token);
  const { isAuthenticated } = useAuth0();
  return useQuery({
    queryKey: ['anchor-types'],
    enabled: isAuthenticated && !!token,
    queryFn: async () => {
      const res = await anchorTypesApi.getAll();
      return res.data.data as AnchorTypeConfig[];
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

// ─── PERSIST CURRENT PRACTICE ─────────────────────────────────────────────────
// Removed: usePersistPractice() used to write the practice selection to a legacy
// 'previsit_current_practice' localStorage key. That key is now an orphan — the
// Zustand auth store already persists currentPractice under 'previsit-auth' (see
// store/index.ts `partialize`), and useCurrentUser() reads that persisted value
// directly. The legacy hook was a redundant second source of truth (and never
// cleared the key on logout), so it's gone. Selection still survives reloads via
// the Zustand persist middleware.
